import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Doughnut, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { designSystem, commonStyles } from '../../../styles/design-system';

ChartJS.register(RadialLinearScale, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend);

const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${designSystem.spacing.xl};
  margin-bottom: ${designSystem.spacing.xl};
`;

const MetricCard = styled(motion.div)`
  ${commonStyles.card}
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designSystem.colors.background.gradient};
  }

  h3 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize.xl};
    margin-bottom: ${designSystem.spacing.lg};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${designSystem.spacing.sm};
  }

  .metric-value {
    font-size: ${designSystem.typography.fontSize['4xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.primary.main};
    margin-bottom: ${designSystem.spacing.sm};
    text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  }

  .metric-subtitle {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    margin-bottom: ${designSystem.spacing.md};
  }

  .metric-change {
    display: inline-flex;
    align-items: center;
    gap: ${designSystem.spacing.xs};
    padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
    border-radius: ${designSystem.borderRadius.full};
    font-size: ${designSystem.typography.fontSize.xs};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    
    &.positive {
      background: ${designSystem.colors.status.success}20;
      color: ${designSystem.colors.status.success};
    }
    
    &.negative {
      background: ${designSystem.colors.status.error}20;
      color: ${designSystem.colors.status.error};
    }
    
    &.neutral {
      background: ${designSystem.colors.secondary.main}20;
      color: ${designSystem.colors.secondary.main};
    }
  }
`;

const ChartContainer = styled.div`
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.xl};
  padding: ${designSystem.spacing.xl};
  margin-bottom: ${designSystem.spacing.xl};
  border: 1px solid ${designSystem.colors.border.light};

  h3 {
    color: ${designSystem.colors.text.primary};
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize.xl};
    margin-bottom: ${designSystem.spacing.lg};
    text-align: center;
  }

  .chart-wrapper {
    position: relative;
    height: 300px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${designSystem.spacing.xl};
  margin-bottom: ${designSystem.spacing.xl};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const DetailMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${designSystem.spacing.md};
  margin-top: ${designSystem.spacing.lg};
  
  .detail-item {
    background: ${designSystem.colors.background.secondary};
    padding: ${designSystem.spacing.md};
    border-radius: ${designSystem.borderRadius.lg};
    border: 1px solid ${designSystem.colors.border.light};
    
    .label {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.xs};
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: ${designSystem.spacing.xs};
    }
    
    .value {
      color: ${designSystem.colors.text.primary};
      font-size: ${designSystem.typography.fontSize.lg};
      font-weight: ${designSystem.typography.fontWeight.bold};
    }
  }
`;

interface AdvancedMetricsProps {
  conversionRate: number;
  conversionChange: number;
  averageStayDuration: number;
  stayChange: number;
  averageRating: number;
  ratingChange: number;
  occupancyRate: number;
  occupancyChange: number;
  repeatGuestRate: number;
  repeatChange: number;
  averageBookingValue: number;
  bookingValueChange: number;
  seasonalData: {
    labels: string[];
    bookings: number[];
    revenue: number[];
  };
  bookingSourceData: {
    direct: number;
    booking: number;
    airbnb: number;
    other: number;
  };
}

const AdvancedMetrics: React.FC<AdvancedMetricsProps> = ({
  conversionRate,
  conversionChange,
  averageStayDuration,
  stayChange,
  averageRating,
  ratingChange,
  occupancyRate,
  occupancyChange,
  repeatGuestRate,
  repeatChange,
  averageBookingValue,
  bookingValueChange,
  seasonalData,
  bookingSourceData,
}) => {
  const getChangeClass = (change: number) => {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return '📈';
    if (change < 0) return '📉';
    return '➡️';
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: designSystem.colors.text.primary,
          font: {
            family: designSystem.typography.fontFamily.primary,
            size: 12,
          },
          padding: 20,
        }
      },
      tooltip: {
        backgroundColor: designSystem.colors.background.primary,
        titleColor: designSystem.colors.text.primary,
        bodyColor: designSystem.colors.text.secondary,
        borderColor: designSystem.colors.border.light,
        borderWidth: 1,
        cornerRadius: 8,
      }
    }
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: designSystem.colors.background.primary,
        titleColor: designSystem.colors.text.primary,
        bodyColor: designSystem.colors.text.secondary,
        borderColor: designSystem.colors.border.light,
        borderWidth: 1,
        cornerRadius: 8,
      }
    },
    scales: {
      r: {
        angleLines: {
          color: designSystem.colors.border.light,
        },
        grid: {
          color: designSystem.colors.border.light,
        },
        pointLabels: {
          color: designSystem.colors.text.primary,
          font: {
            family: designSystem.typography.fontFamily.primary,
            size: 11,
          }
        },
        ticks: {
          color: designSystem.colors.text.secondary,
          backdropColor: 'transparent',
        }
      }
    }
  };

  const sourceData = {
    labels: ['Diretto', 'Booking.com', 'Airbnb', 'Altri'],
    datasets: [
      {
        data: [
          bookingSourceData.direct,
          bookingSourceData.booking,
          bookingSourceData.airbnb,
          bookingSourceData.other,
        ],
        backgroundColor: [
          designSystem.colors.primary.main,
          '#1e40af',
          '#ff5a5f',
          designSystem.colors.secondary.main,
        ],
        borderWidth: 0,
      },
    ],
  };

  const performanceRadarData = {
    labels: [
      'Conversione',
      'Durata Soggiorno',
      'Rating Medio',
      'Occupancy',
      'Ospiti Ricorrenti',
      'Valore Booking'
    ],
    datasets: [
      {
        label: 'Performance KPI',
        data: [
          conversionRate,
          (averageStayDuration / 10) * 100, // Normalizzato
          (averageRating / 5) * 100,
          occupancyRate,
          repeatGuestRate,
          (averageBookingValue / 1000) * 100, // Normalizzato
        ],
        backgroundColor: designSystem.colors.primary.main + '20',
        borderColor: designSystem.colors.primary.main,
        pointBackgroundColor: designSystem.colors.primary.main,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: designSystem.colors.primary.main,
      },
    ],
  };

  return (
    <>
      <MetricsContainer>
        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3>📊 Tasso di Conversione</h3>
          <div className="metric-value">{conversionRate.toFixed(1)}%</div>
          <div className="metric-subtitle">Visite → Prenotazioni</div>
          <div className={`metric-change ${getChangeClass(conversionChange)}`}>
            {getChangeIcon(conversionChange)} {Math.abs(conversionChange).toFixed(1)}% vs mese scorso
          </div>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3>🏠 Durata Media Soggiorno</h3>
          <div className="metric-value">{averageStayDuration.toFixed(1)}</div>
          <div className="metric-subtitle">Notti per prenotazione</div>
          <div className={`metric-change ${getChangeClass(stayChange)}`}>
            {getChangeIcon(stayChange)} {Math.abs(stayChange).toFixed(1)} notti vs mese scorso
          </div>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3>⭐ Rating Medio</h3>
          <div className="metric-value">{averageRating.toFixed(1)}/5</div>
          <div className="metric-subtitle">Valutazione ospiti</div>
          <div className={`metric-change ${getChangeClass(ratingChange)}`}>
            {getChangeIcon(ratingChange)} {Math.abs(ratingChange).toFixed(1)} vs mese scorso
          </div>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3>🏡 Tasso di Occupancy</h3>
          <div className="metric-value">{occupancyRate.toFixed(1)}%</div>
          <div className="metric-subtitle">Notti occupate</div>
          <div className={`metric-change ${getChangeClass(occupancyChange)}`}>
            {getChangeIcon(occupancyChange)} {Math.abs(occupancyChange).toFixed(1)}% vs mese scorso
          </div>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3>🔄 Ospiti Ricorrenti</h3>
          <div className="metric-value">{repeatGuestRate.toFixed(1)}%</div>
          <div className="metric-subtitle">Ritornano da noi</div>
          <div className={`metric-change ${getChangeClass(repeatChange)}`}>
            {getChangeIcon(repeatChange)} {Math.abs(repeatChange).toFixed(1)}% vs mese scorso
          </div>
        </MetricCard>

        <MetricCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h3>💰 Valore Medio Booking</h3>
          <div className="metric-value">€{averageBookingValue.toFixed(0)}</div>
          <div className="metric-subtitle">Per prenotazione</div>
          <div className={`metric-change ${getChangeClass(bookingValueChange)}`}>
            {getChangeIcon(bookingValueChange)} €{Math.abs(bookingValueChange).toFixed(0)} vs mese scorso
          </div>
        </MetricCard>
      </MetricsContainer>

      <ComparisonRow>
        <ChartContainer>
          <h3>🎯 Sorgenti Prenotazioni</h3>
          <div className="chart-wrapper">
            <Doughnut data={sourceData} options={doughnutOptions} />
          </div>
        </ChartContainer>

        <ChartContainer>
          <h3>🔥 Performance Radar</h3>
          <div className="chart-wrapper">
            <Radar data={performanceRadarData} options={radarOptions} />
          </div>
        </ChartContainer>
      </ComparisonRow>

      <ChartContainer>
        <h3>📈 Dettagli Performance</h3>
        <DetailMetrics>
          <div className="detail-item">
            <div className="label">Bounce Rate</div>
            <div className="value">23.4%</div>
          </div>
          <div className="detail-item">
            <div className="label">Tempo Medio sul Sito</div>
            <div className="value">4m 32s</div>
          </div>
          <div className="detail-item">
            <div className="label">Check-in Puntuali</div>
            <div className="value">94.2%</div>
          </div>
          <div className="detail-item">
            <div className="label">Problemi Risolti</div>
            <div className="value">98.7%</div>
          </div>
          <div className="detail-item">
            <div className="label">Tempo Resp. Chat</div>
            <div className="value">12min</div>
          </div>
          <div className="detail-item">
            <div className="label">Upselling Rate</div>
            <div className="value">15.8%</div>
          </div>
        </DetailMetrics>
      </ChartContainer>
    </>
  );
};

export default AdvancedMetrics; 